// Added by Sejune Oh on 2019-08-22
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, RefObject } from 'react'
import { withStyles, Theme, createStyles, WithStyles } from '@material-ui/core'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactEmptyEvent, ReactInputChangeEvent, ReactKeyUpEvent } from '@/helper/define'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import {
    TextField,
    InputAdornment,
    LockIcon,
    VisibilityIcon,
    VisibilityOffIcon,
    Button,
    AccountCircleIcon,
    Typography,
    InfoIcon,
} from '@/components/_primes'

import LockScreenStyle from '@/styles/login/LockScreenStyle'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface PasswordCheckFCProps extends WithStyles<typeof styles> {
    onConfirmBtnClick: ReactEmptyEvent
    onCancelBtnClick: ReactEmptyEvent
    onPasswordInputIconClick: ReactEmptyEvent
    userId: string
    hidePassword: boolean
    onValueChange: ReactInputChangeEvent
    onKeyUp: ReactKeyUpEvent
    btnId: string
    productBI: string
    useLoginFormTopImage: boolean
    loginTopImage: string
    loginBackgroundImage: string
    inputRef: RefObject<HTMLInputElement>
    version: string
    copyRight: string
    desc?: {
        title: string
        content: string
    } | null
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const PasswordCheck: FC<PasswordCheckFCProps> = ({ classes, ...props }) => {
    const labelId = Intl.getIntlText({ id: 'htmljs_react.component.passwordcheck.label_id' })
    const holderId = Intl.getIntlText({ id: 'htmljs_react.component.passwordcheck.holder_id' })
    const labelPw = Intl.getIntlText({ id: 'htmljs_react.component.passwordcheck.label_pw' })
    const btnConfirm = Intl.getIntlText({ id: 'htmljs_react.component.passwordcheck.btn_unlock' })
    const btnCancel = Intl.getIntlText({ id: 'htmljs_react.component.passwordcheck.btn_logout' })

    return (
        <div className={classes.Wrapper}>
            <div className={classes.BGWrapper} style={{ backgroundImage: props.loginBackgroundImage }}></div>
            <div className={classes.ScreenLockWrapper}>
                <div
                    className={classes.ScreenLockLeftWrapper}
                    style={{ backgroundImage: `url(/images/lock.form.left.template_v1.png)` }}
                ></div>
                <form className={classes.ScreenLockRightWrapper}>
                    <div className={classes.TextFieldWrapper}>
                        <div className={classes.HeaderWrapper}>
                            {props.useLoginFormTopImage ? (
                                <div
                                    className={classes.CPLogoImgWrapper}
                                    style={{ backgroundImage: props.loginTopImage }}
                                />
                            ) : (
                                    <div className={classes.CPLogo}>{props.productBI}</div>
                                )}
                        </div>
                        <TextField
                            id="email"
                            className={classes.Input}
                            label={labelId}
                            placeholder={holderId}
                            margin="normal"
                            variant="outlined"
                            type="text"
                            name="email"
                            autoComplete="email nickname"
                            value={props.userId}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircleIcon />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                className: classes.AutoFocusableInput,
                            }}
                            disabled
                        />
                        <TextField
                            id="password"
                            className={classes.Input}
                            label={labelPw}
                            margin="normal"
                            variant="outlined"
                            name="password"
                            onChange={props.onValueChange}
                            autoComplete="current-password"
                            autoFocus={true}
                            type={props.hidePassword ? 'password' : 'text'}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="start" onClick={props.onPasswordInputIconClick}>
                                        {props.hidePassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    </InputAdornment>
                                ),
                                onKeyUp: props.onKeyUp,
                            }}
                            inputProps={{
                                className: classes.AutoFocusableInput,
                            }}
                            inputRef={props.inputRef}
                        />
                    </div>

                    <div className={classes.BottomWrapper}>
                        <div className={classes.BtnWrapper}>
                            <Button
                                id={props.btnId}
                                btnText={btnConfirm}
                                className={classes.LockBtn}
                                buttonType={'task'}
                                onClick={props.onConfirmBtnClick}
                            />
                            <Button btnText={btnCancel} className={classes.LockBtn} onClick={props.onCancelBtnClick} />
                        </div>
                        {props.desc && (
                            <div className={classes.PWCheckDescWrapper}>
                                <div className={classes.PWCheckDescTitleWrapper}>
                                    <InfoIcon className={classes.PWCheckDescIcon} />
                                    <span className={classes.PWCheckDescTitle}>{props.desc.title}</span>
                                </div>
                                <div className={classes.PWCheckDescContent}>{props.desc.content}</div>
                            </div>
                        )}
                    </div>
                </form>

                <Typography className={classes.LoginPaperCopyRight} align="center">
                    {props.copyRight} ({props.version})
                </Typography>
            </div>
        </div>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        ...LockScreenStyle(theme),
    })

export default withStyles(styles, { withTheme: true })(PasswordCheck)
